import { useEffect, useMemo, useState } from "react"
import { Box, HStack, IconButton, Separator, SimpleGrid, Spinner, Stack } from "@chakra-ui/react"
import { LuCheck, LuFileText, LuPaperclip, LuRefreshCw, LuUpload } from "react-icons/lu"
import { FiInfo } from "react-icons/fi"
import { downloadDocument } from "@/api/documents"
import { Button, MiscUploadModal, Section, Text, Toaster, toaster, UploadModal } from "@/components"
import { ALL_STEPS, BC_FORM_STEP_INDEX, SIGN_CONTRACT_STEP_INDEX } from "@/constants"
import { useAuth } from "@/contexts"
import { useDropboxDocuments, useUploadDocuments } from "@/hooks"
import { Deal, DealStep } from "@/types"
import { DocumentsTable } from "./components"
import { documentContainsDocType, documentContainsAcceptedDocType } from "./utils"

interface Props {
  isLoading: boolean
  deals: Deal[]
  onRefresh?: () => void
  currentDeal: Deal | null
  onDealChange: (deal: Deal) => void
}

const WHITE_LISTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword"
]

const PRE_OFFER_DOC_TYPES = [
  // "Option Grant Information",
  "Equity Holdings (ISO, NSO, Shares, RSUs)",
  "FMV (409A) Confirmation",
  // "Stock Certificate",
]

const POST_BC_DOC_TYPES = [
  "ID - Passport",
  "ID - Driver's License",
  "Stock Option Plan",
  "Stock Option Grant"
]

const DOC_TYPE_TO_PREFIX: Record<string, string> = {
  // "Option Grant Information": "Option_Grant",
  "FMV (409A) Confirmation": "FMV Confirmation",
  // "Stock Certificate": "Stock_Certificate",
  "Equity Holdings (ISO, NSO, Shares, RSUs)": "Equity Holdings",
  "ID - Passport": "ID - Passport",
  "ID - Driver's License": "ID - Drivers License",
  "Stock Option Plan": "SOP",
  "Stock Option Grant": "SOGA",
}

// Legacy prefixes for backward compatibility with files uploaded before the "ID - " prefix was added
const DOC_TYPE_LEGACY_PREFIXES: Record<string, string[]> = {
  "ID - Passport": ["passport"],
  "ID - Driver's License": ["drivers license", "drivers_license"],
}

const DOC_TYPE_DESCRIPTIONS: Record<string, string> = {
  "Equity Holdings (ISO, NSO, Shares, RSUs)": "Grant documents or screenshots showing vested amounts, dates, and strike prices.",
  "FMV (409A) Confirmation": "Current 409A share price, find it in your equity portal or ask your stock plan administrator.",
  "ID - Passport": "A clear photo or scan of your passport ID page",
  "ID - Driver's License": "Front of your current driver's license",
  "Stock Option Plan": "The plan document from your employer, usually in your equity portal.",
  "Stock Option Grant": "The grant document from your employer, usually in your equity portal.",
}

const DOC_TYPE_DISPLAY_NAMES: Record<string, string> = {
  "Equity Holdings (ISO, NSO, Shares, RSUs)": "Equity Holdings (ISO, NSO, Shares, RSUs)",
  "FMV (409A) Confirmation": "FMV (409A) Confirmation",
  "ID - Passport": "Passport",
  "ID - Driver's License": "Driver's License",
  "Stock Option Plan": "Stock Option Plan (SOP)",
  "Stock Option Grant": "Stock Option Grant Agreement (SOGA)",
}

const STATIC_DOC_TYPE_TOOLTIPS: Record<string, React.ReactNode> = {
  "Equity Holdings (ISO, NSO, Shares, RSUs)": (
    <>
      <Text fontSize="xs" mb={1}>Options (ISOs, NSOs): vested amounts, grant dates, strike prices.</Text>
      <Text fontSize="xs" mb={1}>Shares: stock certificate or vesting confirmation.</Text>
      <Text fontSize="xs">RSUs: vested amounts, grant dates, expiration date.</Text>
    </>
  ),
  "FMV (409A) Confirmation": (
    <>
      <Text fontSize="xs" mb={1}>Find it in your equity portal:</Text>
      <Box as="ul" pl={4} fontSize="xs" mb={1}>
        <li>Carta — Portfolio page or bottom of the Exercise Simulator page</li>
        <li>Shareworks — Dashboard</li>
        <li>Other portals — typically on a summary page</li>
      </Box>
      <Text fontSize="xs">If capturing a screenshot, make sure your computer's date and time are visible in the image. If you can't locate the FMV, ask your stock plan administrator or CFO for the current 409A value.</Text>
    </>
  ),
  "ID - Passport": (
    <Text fontSize="xs">If your Passport is non-US, please upload an active VISA or Green Card.</Text>
  ),
  "Stock Option Plan": (
    <Text fontSize="xs">The master plan document governing your stock options, issued by your employer. Usually found in your equity portal. If unavailable, request it from your stock plan administrator or HR.</Text>
  ),
  "Stock Option Grant": (
    <Text fontSize="xs">Your grant agreement showing the number of options, grant date, vesting schedule, and strike price. Typically found in your equity portal (Carta, Shareworks, etc.). If unavailable, contact your stock plan administrator.</Text>
  ),
}

export const DocumentsComponent = ({ isLoading, deals, onRefresh, currentDeal, onDealChange }: Props) => {
  const { user } = useAuth()
  const isDM = user?.userType === "deal_manager"
  const [stage, setStage] = useState<"PRE_OFFER" | "POST_BC" | "CLOSED">("CLOSED");
  const [docTypes, setDocTypes] = useState<string[]>(PRE_OFFER_DOC_TYPES);
  const { mutate: uploadDocuments, isPending, isSuccess } = useUploadDocuments();
  const isDealInactive = currentDeal?.isActive === 0
  const isReadOnly = isDealInactive

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string>("");
  const [isMiscModalOpen, setIsMiscModalOpen] = useState(false);

  // Tooltip expand state
  const [expandedTooltip, setExpandedTooltip] = useState<string | null>(null);

  const clientAddress = useMemo(() => {
    if (user?.userType === "client") {
      return [user.addressLine1, user.city, user.state, user.zip].filter(Boolean).join(", ")
    }
    return ""
  }, [user])

  const getTooltipContent = (docType: string): React.ReactNode | null => {
    if (docType === "ID - Driver's License") {
      return (
        <Text fontSize="xs">
          If the address on your license does not match your address on file{clientAddress ? ` (${clientAddress})` : ""}, please upload a Utility Bill or other document confirming your current address.
        </Text>
      )
    }
    return STATIC_DOC_TYPE_TOOLTIPS[docType] ?? null
  }

  useEffect(() => {
    if (currentDeal) {
      if (currentDeal.currentStep === DealStep.CLOSED) {
        setStage("CLOSED");
        return;
      }

      if (currentDeal.currentStep === DealStep.COMPLETE_BC_FORM
        || currentDeal.currentStep === DealStep.SIGN_BC
        || currentDeal.currentStep === DealStep.UPLOAD_CLOSING_DOCS
        || currentDeal.currentStep === DealStep.CLOSING) {
        setStage("POST_BC");
        setDocTypes(POST_BC_DOC_TYPES);
        return;
      } else {
        setStage("PRE_OFFER");
        setDocTypes(PRE_OFFER_DOC_TYPES);
        return;
      }
    }
  }, [currentDeal]);

  const { data: dropboxDocuments, isLoading: isLoadingDocs } = useDropboxDocuments(currentDeal?.id ?? undefined);

  const getDocTypePrefixes = (docType: string): string[] => {
    const primary = DOC_TYPE_TO_PREFIX[docType]?.toLowerCase();
    if (!primary) return [];
    const legacy = DOC_TYPE_LEGACY_PREFIXES[docType] ?? [];
    return [primary, ...legacy];
  }

  const hasDocTypeUploaded = (docType: string): boolean => {
    const prefixes = getDocTypePrefixes(docType);
    if (prefixes.length === 0 || !dropboxDocuments) return false;
    return dropboxDocuments.some((doc) =>
      documentContainsDocType(doc.name, prefixes)
    );
  }

  const isDocTypeAccepted = (docType: string): boolean => {
    const prefixes = getDocTypePrefixes(docType);
    if (prefixes.length === 0 || !dropboxDocuments) return false;
    return dropboxDocuments.some((doc) =>
      documentContainsAcceptedDocType(doc.name, prefixes)
    );
  }

  // Called when user clicks a doc type button — opens the upload modal
  const handleDocTypeClick = (docType: string) => {
    setSelectedDocType(docType);
    setIsModalOpen(true);
  }

  // Called when user clicks "Upload" inside the modal drop zone
  const handleConfirmUpload = (filesToUpload: File[]) => {
    if (!currentDeal || !selectedDocType) return;
    for (const file of filesToUpload) {
      if (!WHITE_LISTED_FILE_TYPES.includes(file.type)) {
        toaster.create({
          title: `${file.type} is not a valid file type`,
          type: "error",
          closable: true,
          duration: 3000,
        })
        return
      }
    }
    const documentType = DOC_TYPE_TO_PREFIX[selectedDocType];

    uploadDocuments(
      { documentType, dealIdStr: currentDeal.id.toString(), files: filesToUpload, clientId: isDM ? currentDeal.client?.id : undefined },
      {
        onSuccess: () => {
          toaster.create({
            title: "Files uploaded successfully",
            description: `Your files have been renamed to "${documentType}" for organization purposes.`,
            type: "success",
            duration: 10000,
          });
          setIsModalOpen(false);
        },
        onError: () => {
          toaster.create({
            title: "Upload failed. Please contact your equity partner.",
            type: "error",
            duration: 3000,
          });
        },
      }
    )
  }

  const handleMiscUpload = (file: File, customName: string) => {
    if (!currentDeal) return
    if (!WHITE_LISTED_FILE_TYPES.includes(file.type)) {
      toaster.create({
        title: `${file.type} is not a valid file type`,
        type: "error",
        closable: true,
        duration: 3000,
      })
      return
    }
    uploadDocuments(
      { documentType: "Misc", dealIdStr: currentDeal.id.toString(), files: [file], customFileName: customName, clientId: isDM ? currentDeal.client?.id : undefined },
      {
        onSuccess: () => {
          toaster.create({
            title: "File uploaded successfully",
            type: "success",
            duration: 5000,
          });
          setIsMiscModalOpen(false);
        },
        onError: () => {
          toaster.create({
            title: "Upload failed. Please contact your deal manager.",
            type: "error",
            duration: 3000,
          });
        },
      }
    )
  }

  const handleDownloadDocument = async (name: string) => {
    try {
      if (!currentDeal?.id) return;
      const blob = await downloadDocument({ dealId: currentDeal.id, name });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error in useDownloadDocument:", error);
      throw error;
    }
  }

  const renderDocumentsTable = () => {
    if (!currentDeal) {
      return null
    }

    if (isLoadingDocs || isLoading) {
      return (
        <Spinner />
      )
    }

    return (
      <DocumentsTable documents={dropboxDocuments} handleDownloadDocument={handleDownloadDocument} />
    )
  }

  return (
    <Section>
      <HStack>
        <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
          Documents
        </Text>
        {onRefresh && <IconButton aria-label="Refresh" variant="ghost" size="sm" onClick={onRefresh}><LuRefreshCw /></IconButton>}
      </HStack>
      <HStack my={3} gap={2}>
        {
          isLoading && (
            <Spinner />
          )
        }
        {deals.length === 1 ? (
          <HStack>
            <Text fontSize="sm" fontWeight="semibold" color="gray.600" borderWidth={1} borderColor="gray.200" borderRadius="md" px={4} py={2}>
              {deals[0].company.name}
            </Text>
            {deals[0].isActive === 0 && (
              <Box px={2} py={0.5} borderRadius="full" bg="gray.100">
                <Text fontSize="xs" fontWeight="semibold" color="gray.500">Inactive</Text>
              </Box>
            )}
          </HStack>
        ) : (
          deals.map((deal, index) => (
            <HStack key={index} gap={1}>
              <Button
                variant={currentDeal?.id === deal.id ? "solid" : "outline"}
                onClick={() => onDealChange(deal)}
                opacity={deal.isActive === 0 ? 0.6 : 1}
              >
                {deal.company.name}
              </Button>
              {deal.isActive === 0 && (
                <Box px={2} py={0.5} borderRadius="full" bg="gray.100">
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500">Inactive</Text>
                </Box>
              )}
            </HStack>
          ))
        )}
      </HStack>
      {isReadOnly ? (
        <>
          { renderDocumentsTable() }
        </>
      ) : (
        <>
          {
            currentDeal &&
            hasDocTypeUploaded("Equity Holdings (ISO, NSO, Shares, RSUs)") &&
            (!currentDeal.offers || currentDeal.offers.length === 0) && (
              <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={4} py={3} mb={4}>
                <Text fontSize="sm" color="blue.700">
                  Thank you for uploading your documents. We're currently working on your deal. You'll be notified when your offer is ready.
                </Text>
              </Box>
            )
          }
          {
            currentDeal &&
            currentDeal.currentStep === DealStep.UPLOAD_CLOSING_DOCS &&
            POST_BC_DOC_TYPES.every((docType) => hasDocTypeUploaded(docType)) && (
              <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={4} py={3} mb={4}>
                <Text fontSize="sm" color="blue.700">
                  Thank you for uploading your documents. We are reviewing your documents and finalizing your contract. You'll be notified when it's ready for review.
                </Text>
              </Box>
            )
          }
          <Separator mb={5} />
          {
            stage !== "CLOSED" && (
              <>
                <HStack justifyContent="space-between" mb={3}>
                  <Text fontSize="md" fontWeight="bold">Required for this stage</Text>
                  <Text fontSize="sm" color="gray.500">Click any card to upload</Text>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  {docTypes.map((docType) => {
                    const uploaded = hasDocTypeUploaded(docType);
                    const accepted = uploaded && isDocTypeAccepted(docType);
                    const pending = uploaded && !accepted;
                    const displayName = DOC_TYPE_DISPLAY_NAMES[docType] ?? docType;
                    const description = DOC_TYPE_DESCRIPTIONS[docType] ?? "";

                    const cardBg = accepted ? "green.50" : pending ? "yellow.50" : "white";
                    const cardBorderColor = accepted ? "green.300" : pending ? "yellow.300" : "gray.200";
                    const iconBg = accepted ? "green.100" : pending ? "yellow.100" : "gray.100";

                    return (
                      <Stack
                        key={docType}
                        p={5}
                        borderWidth="1px"
                        borderRadius="lg"
                        borderColor={cardBorderColor}
                        bg={cardBg}
                        gap={3}
                        cursor="pointer"
                        onClick={() => handleDocTypeClick(docType)}
                        _hover={{ boxShadow: "md" }}
                      >
                        <HStack align="flex-start">
                          <Box borderRadius="full" p={2} bg={iconBg} flexShrink={0}>
                            {accepted
                              ? <LuCheck size={20} color="var(--chakra-colors-green-600)" />
                              : <LuFileText size={20} color="var(--chakra-colors-gray-500)" />}
                          </Box>
                          <Stack gap={0}>
                            <HStack gap={0}>
                              <Text fontSize="md" fontWeight="bold">{displayName}</Text>
                              {getTooltipContent(docType) && (
                                <Box
                                  as="span"
                                  cursor="pointer"
                                  display="inline-flex"
                                  alignItems="center"
                                  ml={1}
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    setExpandedTooltip(expandedTooltip === docType ? null : docType)
                                  }}
                                >
                                  <FiInfo size={14} color={expandedTooltip === docType ? "#3182CE" : "#718096"} />
                                </Box>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="gray.500">{description}</Text>
                            {expandedTooltip === docType && getTooltipContent(docType) && (
                              <Box mt={2} px={3} py={2} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100">
                                {getTooltipContent(docType)}
                              </Box>
                            )}
                          </Stack>
                        </HStack>
                        <HStack justifyContent="space-between" mt="5">
                          <HStack gap={2}>
                            {!uploaded && (
                              <Box px={3} py={1} borderRadius="full" bg="red.50" borderWidth="1px" borderColor="red.200">
                                <Text fontSize="xs" color="red.600" fontWeight="semibold">REQUIRED</Text>
                              </Box>
                            )}
                            {pending && (
                              <Box px={3} py={1} borderRadius="full" bg="yellow.100" borderWidth="1px" borderColor="yellow.300">
                                <Text fontSize="xs" color="yellow.700" fontWeight="semibold">Pending Review</Text>
                              </Box>
                            )}
                            {accepted && (
                              <Box px={3} py={1} borderRadius="full" bg="green.100">
                                <Text fontSize="xs" color="green.700" fontWeight="semibold">Accepted</Text>
                              </Box>
                            )}
                          </HStack>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDocTypeClick(docType); }}
                          >
                            <LuUpload /> Upload
                          </Button>
                        </HStack>
                      </Stack>
                    );
                  })}
                </SimpleGrid>
              </>
            )
          }
          <Box mt={stage !== "CLOSED" ? 6 : 0}>
            <Text fontSize="md" fontWeight="bold" mb={3}>Miscellaneous</Text>
            <Stack
              p={5}
              borderWidth="1px"
              borderRadius="lg"
              borderColor="gray.200"
              bg="white"
              gap={3}
              cursor="pointer"
              onClick={() => setIsMiscModalOpen(true)}
              _hover={{ boxShadow: "md" }}
              maxW={{ base: "100%", md: "calc(50% - 8px)" }}
            >
              <HStack align="flex-start">
                <Box borderRadius="full" p={2} bg="gray.100" flexShrink={0}>
                  <LuPaperclip size={20} color="var(--chakra-colors-gray-500)" />
                </Box>
                <Stack gap={0}>
                  <Text fontSize="md" fontWeight="bold">Upload a Document</Text>
                  <Text fontSize="xs" color="gray.500">Upload any additional document not listed above.</Text>
                </Stack>
              </HStack>
              <HStack justifyContent="flex-end" mt="5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setIsMiscModalOpen(true); }}
                >
                  <LuUpload /> Upload
                </Button>
              </HStack>
            </Stack>
          </Box>

          { renderDocumentsTable() }

          <UploadModal
            isOpen={isModalOpen}
            onOpenChange={setIsModalOpen}
            title={`Upload ${DOC_TYPE_DISPLAY_NAMES[selectedDocType] ?? selectedDocType}`}
            description={DOC_TYPE_DESCRIPTIONS[selectedDocType]}
            tooltipContent={getTooltipContent(selectedDocType)}
            uploadDocuments={handleConfirmUpload}
            isLoading={isPending}
            isSuccess={isSuccess}
          />

          <MiscUploadModal
            isOpen={isMiscModalOpen}
            onOpenChange={setIsMiscModalOpen}
            onUpload={handleMiscUpload}
            isLoading={isPending}
            isSuccess={isSuccess}
          />

          <Toaster />
        </>
      )}
    </Section>
  )
}
